// ReservationOverview.tsx
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    useTheme,
    useMediaQuery,
    Grid,
    Card,
    CardContent,
    ToggleButtonGroup,
    ToggleButton,
    Stack,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    AccordionDetails,
    Accordion,
    AccordionSummary,
    Box,
    Container,
    Skeleton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ReservationEdit from './ReservationEdit';
import { useDetailRoute } from '../hooks/useDetailRoute';
import { IReservation } from '../Interfaces/IReservation';
import { useGetReservation, useGetReservationStatus, useGetReservationById } from '../hooks/reservations';
import { useGetUsers } from '../hooks/users';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BrandedDataGridOverlay } from '../core/BrandedDataGridOverlay';
import RowActionsMenu from '../core/RowActionsMenu';
import AuditInfoDialog from '../core/AuditInfoDialog';
import DetailScreenHeader from '../core/DetailScreenHeader';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../calendar/dialogLayout';
import { checkPermission } from '../utils/permission';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDeleteReservation } from '../hooks/reservations';
import { useAlert } from '../core/AlertContext';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';

import { GREEN_800, GREEN_900, cardLgSx as cardSx, primaryButtonSx, iconCircleSx } from '../core/pvtaStyles';
import PageHeader from '../core/PageHeader';

// Hilfsfunktion zum Formatieren des Datums
const formatDate = (dateStr: string): string => {
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

// Key-Value-Zeile für die mobile Buchungskarte.
const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            py: 0.5,
        }}
    >
        <Typography variant="body2" sx={{ color: 'var(--pvta-muted)', flexShrink: 0 }}>
            {label}
        </Typography>
        <Box sx={{ textAlign: 'right', minWidth: 0, fontWeight: 600, color: 'var(--pvta-text)' }}>
            {children}
        </Box>
    </Box>
);

export const ReservationOverview: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Nur fuer die noch nicht gespeicherte neue Buchung — die hat keine ID und
    // damit keine URL; bestehende Buchungen haengen an der Route.
    const [isCreating, setIsCreating] = useState(false);
    const { detailId, hasInvalidId, openDetail, closeDetail } = useDetailRoute('/myreservations');
    const [infoCandidate, setInfoCandidate] = useState<IReservation | null>(null);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const isAdmin = checkPermission(2, 3);

    const { reservationArray, isLoadingReservationArray } = useGetReservation({ includeDeleted });
    const { deleteReservation } = useDeleteReservation();
    const { showAlert } = useAlert();
    const { userArray, isLoadingUserArray } = useGetUsers();
    const { reservationStatusArray, isLoadingReservationStatusArray } = useGetReservationStatus();

    // Detail-Endpunkt als maszgebliche Quelle: die Liste haengt an Status-/Zeitfiltern
    // und am `include_deleted`-Schalter, ein per Deep-Link angesteuerter Datensatz
    // waere dort je nach Ansicht faelschlich nicht auffindbar. Der Treffer aus der
    // Liste dient nur als Startwert, damit der Dialog nicht leer aufblitzt.
    const { reservation: fetchedReservation, isFetchedReservation, isErrorReservation } =
        useGetReservationById(detailId);
    const listReservation = detailId === null
        ? null
        : (reservationArray.find((res) => res.id === detailId) ?? null);
    // `isCreating` gewinnt: sonst zeigte der Dialog beim Anlegen noch den per URL
    // geoeffneten Datensatz.
    const selectedReservation = (isCreating || detailId === null)
        ? null
        : (fetchedReservation ?? listReservation);
    const open = isCreating || selectedReservation !== null;

    const sortedReservations = [...reservationArray].sort((a, b) => b.id - a.id);

    //Filterung der Reservierungen
    const [statusFilter, setStatusFilter] = useState<number | 'all'>('all');

    const handleStatusFilterChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setStatusFilter(value === 'all' ? 'all' : Number(value));
    };



    const [filterMode, setFilterMode] = useState<'all' | 'future' | 'past'>('all');
    const today = new Date();

    const filteredReservations = sortedReservations.filter((res) => {
        const resEndDate = new Date(res.end_date);
        const matchesDate =
            filterMode === 'all' ||
            (filterMode === 'future' && resEndDate >= today) ||
            (filterMode === 'past' && resEndDate < today);

        const matchesStatus =
            statusFilter === 'all' || res.booking_status?.id === statusFilter;

        return matchesDate && matchesStatus;
    });


    const handleFilterChange = (
        event: React.MouseEvent<HTMLElement>,
        newFilter: 'all' | 'future' | 'past' | null
    ) => {
        if (newFilter !== null) {
            setFilterMode(newFilter);
        }
    };


    const handleDeleteReservation = async (id: number) => {
        try {
            await deleteReservation(id);
            showAlert({ text: 'Buchung erfolgreich gelöscht.' });
        } catch {
            showAlert({ text: 'Fehler beim Löschen der Buchung.', severity: 'error' });
        }
    };

    // Deep-Link auf eine unbekannte oder fremde Buchung: zurueck auf die Uebersicht
    // statt eines leeren Dialogs. Das Backend schraenkt `/reservations/<id>/` bereits
    // auf die eigenen Buchungen ein (Admins und Escheck-Team sehen alle).
    const detailUnavailable = detailId !== null
        && (isErrorReservation || (isFetchedReservation && fetchedReservation === null));

    useEffect(() => {
        if (!hasInvalidId && !detailUnavailable) {
            return;
        }
        showAlert({
            severity: 'error',
            text: 'Diese Buchung existiert nicht oder ist nicht für dich einsehbar.',
        });
        closeDetail();
    }, [closeDetail, detailUnavailable, hasInvalidId, showAlert]);

    const handleNewReservation = () => {
        // Eine offene Detail-Route mitnehmen, sonst taucht der alte Dialog nach dem
        // Abbrechen wieder auf.
        if (detailId !== null) {
            closeDetail();
        }
        setIsCreating(true);
    };

    const handleEditReservation = (reservation: IReservation) => {
        openDetail(reservation.id);
    };

    /**
     * Schliesst den Dialog: bricht das Anlegen ab, sonst zurueck auf die Uebersicht.
     * Absolut statt `navigate(-1)` — bei einem Link aus einer Mail gibt es keinen
     * App-Verlauf, ein Schritt zurueck wuerde die Anwendung verlassen.
     */
    const handleClose = () => {
        if (isCreating) {
            setIsCreating(false);
            return;
        }
        closeDetail();
    };

    const getUserName = (userId: number): string => {
        if (!userArray) return '';
        const user = userArray.find((u) => u.id === userId);
        if (!user) return '';
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        return name || user.username;
    };

    // Farb-Definitionen für Status
    const customColors: { [key: number]: { bg: string; text: string; border: string } } = {
        1: { bg: '#fff3cd', text: '#856404', border: '#f8f8f8' }, // Angefragt
        2: { bg: '#d1ecf1', text: '#0c5460', border: '#f8f8f8' }, // Genehmigt
        3: { bg: '#f8d7da', text: '#721c24', border: '#f8f8f8' }, // Abgelehnt
        4: { bg: '#c0e7ca', text: '#11441d', border: '#f8f8f8' }, // Abgerechnet
        5: { bg: '#70e18c', text: '#11441d', border: '#f8f8f8' }, // Abrechnung genehmigt
        6: { bg: '#2ae358', text: '#11441d', border: '#f8f8f8' }, // Gezahlt
    };

    const formatDateTime = (dateTimeStr?: string | null): string => {
        if (!dateTimeStr) {
            return '-';
        }
        const date = new Date(dateTimeStr);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    };

    const columns: GridColDef<IReservation>[] = [
        { field: 'id', headerName: 'Nr', width: 100 },
        {
            field: 'start_date',
            headerName: 'Von',
            width: 150,
            renderCell: (params) => <span>{formatDate(params.value as string)}</span>,
        },
        {
            field: 'end_date',
            headerName: 'Bis',
            width: 150,
            renderCell: (params) => <span>{formatDate(params.value as string)}</span>,
        },
        {
            field: 'account',
            headerName: 'Angefragt von',
            width: 200,
            renderCell: (params) => <span>{getUserName(params.value as number)}</span>,
        },
        {
            field: 'booking_status',
            headerName: 'Buchungsstatus',
            width: 150,
            renderCell: (params) => {
                const status = params.value;
                const colors = status?.id && customColors[status.id]
                    ? customColors[status.id]
                    : { bg: 'grey', text: 'white', border: 'grey' };
                return (
                    <Chip
                        label={status?.name}
                        variant="outlined"
                        sx={{
                            bgcolor: colors.bg,
                            color: colors.text,
                            borderColor: colors.border,
                            fontWeight: 'bold',
                        }}
                    />
                );
            },
        },
        { field: 'amount_rooms', headerName: 'Anzahl Zimmer', width: 150 },
        {
            field: 'created_at',
            headerName: 'Erstellt am',
            width: 170,
            renderCell: (params) => (
                <span>{formatDateTime(params.value as string)}</span>
            ),
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            sortable: false,
            filterable: false,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<IReservation>) => {
                const isDeleted = !!params.row.deleted_at;
                const items = [
                    {
                        label: 'Bearbeiten',
                        icon: <EditOutlinedIcon fontSize="small" />,
                        onClick: () => handleEditReservation(params.row),
                        disabled: isDeleted,
                    },
                    {
                        label: 'Weitere Informationen',
                        icon: <InfoOutlinedIcon fontSize="small" />,
                        onClick: () => setInfoCandidate(params.row),
                    },
                ];
                if (isAdmin && !isDeleted) {
                    items.push({
                        label: 'Löschen',
                        icon: <DeleteOutlineIcon fontSize="small" />,
                        onClick: () => handleDeleteReservation(params.row.id),
                    });
                }
                return <RowActionsMenu items={items} />;
            },
        },
    ];

    if (isLoadingReservationArray || isLoadingUserArray || isLoadingReservationStatusArray) {
        return (
            <Box sx={{ py: { xs: 3, md: 4 } }}>
                <Container maxWidth="xl">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'flex-start', md: 'center' },
                            justifyContent: 'space-between',
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Skeleton variant="text" width={170} height={44} />
                        <Skeleton variant="rounded" width={190} height={36} />
                    </Box>
                    <Skeleton variant="rounded" height={72} sx={{ mb: 2 }} />
                    {!isMobile ? (
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <Skeleton variant="rounded" height={420} />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {[0, 1, 2].map((item) => (
                                <Grid item xs={12} key={item}>
                                    <Card sx={{ p: 1 }}>
                                        <CardContent>
                                            <Skeleton variant="text" width="40%" height={28} />
                                            <Skeleton variant="text" width="55%" />
                                            <Skeleton variant="text" width="55%" />
                                            <Skeleton variant="text" width="70%" />
                                            <Skeleton variant="rounded" width={110} height={24} sx={{ mt: 0.5, mb: 0.5 }} />
                                            <Skeleton variant="text" width="45%" />
                                            <Skeleton variant="text" width="60%" />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Container>
            </Box>
        );
    }

    const hasReservations = reservationArray && reservationArray.length > 0;

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl">
                <PageHeader
                    title="Buchungen"
                    action={hasReservations && (
                        <Button
                            onClick={handleNewReservation}
                            startIcon={<AddRoundedIcon />}
                            sx={{ ...primaryButtonSx, width: { xs: '100%', md: 'auto' } }}
                        >
                            Neue Buchung erstellen
                        </Button>
                    )}
                />

                {!hasReservations ? (
                    <Box
                        sx={{
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                            backgroundColor: '#fbfaf8',
                            p: { xs: 2.5, md: 3 },
                            boxShadow: '0px 12px 28px rgba(15, 23, 42, 0.08)',
                        }}
                    >
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            Keine Buchungen vorhanden.
                        </Typography>
                        <Button
                            onClick={handleNewReservation}
                            startIcon={<AddRoundedIcon />}
                            sx={{ ...primaryButtonSx, mt: 2 }}
                        >
                            Neue Buchung erstellen
                        </Button>

                        {/* Dialog bleibt gemountet, damit der Button funktioniert */}
                        <Dialog
                            open={open}
                            onClose={handleClose}
                            maxWidth={EVENT_DIALOG_MAX_WIDTH}
                            fullWidth
                            fullScreen={isMobile}
                            PaperProps={EVENT_DIALOG_PAPER_SX}
                        >
                            <DetailScreenHeader
                                title={selectedReservation ? `Buchung #${selectedReservation.id}` : 'Neue Buchung erstellen'}
                                onClose={handleClose}
                            />
                            <DialogContent sx={{ p: 0, backgroundColor: 'var(--pvta-bg)' }}>
                                <ReservationEdit onClose={handleClose} initialReservation={selectedReservation} showHeading={false} />
                            </DialogContent>
                        </Dialog>
                    </Box>
                ) : (
                    <>
                        <Accordion
                            sx={{
                                ...cardSx,
                                mb: 2,
                                '&:before': { display: 'none' },
                                overflow: 'hidden',
                            }}
                            elevation={0}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: GREEN_800 }} />}
                                aria-controls="filter-content"
                                id="filter-header"
                                sx={{ px: { xs: 2, md: 2.5 }, minHeight: 60, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box sx={iconCircleSx}>
                                        <FilterListRoundedIcon fontSize="small" />
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, color: GREEN_900, fontSize: 16 }}>
                                        Filter
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pt: 0 }}>
                                <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
                                    <ToggleButtonGroup
                                        value={filterMode}
                                        exclusive
                                        onChange={handleFilterChange}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#f7f4ef',
                                            borderRadius: 999,
                                            p: 0.5,
                                        }}
                                    >
                                        <ToggleButton value="past">Vergangene</ToggleButton>
                                        <ToggleButton value="all">Alle</ToggleButton>
                                        <ToggleButton value="future">Zukünftige</ToggleButton>
                                    </ToggleButtonGroup>

                                    <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={statusFilter.toString()}
                                            onChange={handleStatusFilterChange}
                                            label="Status"
                                        >
                                            <MenuItem value="all">Alle</MenuItem>
                                            {reservationStatusArray.map((status) => (
                                                <MenuItem key={status.id} value={status.id.toString()}>
                                                    {status.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {isAdmin && (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={includeDeleted}
                                                    onChange={(e) => setIncludeDeleted(e.target.checked)}
                                                    size="small"
                                                />
                                            }
                                            label="Gelöschte anzeigen"
                                        />
                                    )}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>

                        {/* Desktop DataGrid */}
                        {!isMobile && (
                            <Box sx={{ ...cardSx, width: '100%', overflowX: 'auto', p: 1 }}>
                                <DataGrid
                                    rows={filteredReservations}
                                    columns={columns}
                                    autoHeight
                                    onRowClick={(params) => handleEditReservation(params.row as IReservation)}
                                    slots={{
                                        noRowsOverlay: BrandedDataGridOverlay,
                                        noResultsOverlay: BrandedDataGridOverlay,
                                    }}
                                    sx={{
                                        border: 0,
                                        '& .MuiDataGrid-row': {
                                            cursor: 'pointer',
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        {/* Mobile Card-Ansicht */}
                        {isMobile && (
                            <Grid container spacing={2}>
                                {filteredReservations.map((res) => {
                                    const bs = res.booking_status;
                                    const colors = bs && bs.id && customColors[bs.id]
                                        ? customColors[bs.id]
                                        : { bg: 'grey', text: 'white', border: 'grey' };
                                    const statusLabel = bs
                                        ? reservationStatusArray.find((s) => s.id === bs.id)?.name ?? bs.name
                                        : 'Unbekannt';
                                    return (
                                        <Grid item xs={12} sm={6} key={res.id}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    ...cardSx,
                                                    cursor: 'pointer',
                                                    height: '100%',
                                                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 'var(--pvta-shadow-hover)',
                                                    },
                                                }}
                                                onClick={() => handleEditReservation(res)}
                                            >
                                                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            mb: 1.5,
                                                        }}
                                                    >
                                                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                                                            <Box sx={iconCircleSx}>
                                                                <EventNoteRoundedIcon fontSize="small" />
                                                            </Box>
                                                            <Typography sx={{ fontWeight: 700, fontSize: 18, color: GREEN_900 }}>
                                                                Buchung #{res.id}
                                                            </Typography>
                                                        </Stack>
                                                        <Box
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                            }}
                                                        >
                                                            <RowActionsMenu
                                                                items={[
                                                                    {
                                                                        label: 'Bearbeiten',
                                                                        icon: <EditOutlinedIcon fontSize="small" />,
                                                                        onClick: () => handleEditReservation(res),
                                                                        disabled: !!res.deleted_at,
                                                                    },
                                                                    {
                                                                        label: 'Weitere Informationen',
                                                                        icon: <InfoOutlinedIcon fontSize="small" />,
                                                                        onClick: () => setInfoCandidate(res),
                                                                    },
                                                                    ...(isAdmin && !res.deleted_at ? [{
                                                                        label: 'Löschen',
                                                                        icon: <DeleteOutlineIcon fontSize="small" />,
                                                                        onClick: () => handleDeleteReservation(res.id),
                                                                    }] : []),
                                                                ]}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <DetailRow label="Von">{formatDate(res.start_date)}</DetailRow>
                                                        <DetailRow label="Bis">{formatDate(res.end_date)}</DetailRow>
                                                        <DetailRow label="Angefragt von">{getUserName(res.account)}</DetailRow>
                                                        <DetailRow label="Status">
                                                            <Chip
                                                                label={statusLabel}
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: colors.bg,
                                                                    color: colors.text,
                                                                    borderColor: colors.border,
                                                                    fontWeight: 700,
                                                                    borderRadius: 999,
                                                                }}
                                                            />
                                                        </DetailRow>
                                                        <DetailRow label="Anzahl Zimmer">{res.amount_rooms}</DetailRow>
                                                        <DetailRow label="Erstellt am">{formatDateTime(res.created_at ?? '')}</DetailRow>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}

                        {/* Dialog bleibt gemountet, damit open funktioniert */}
                        <Dialog
                            open={open}
                            onClose={handleClose}
                            maxWidth={EVENT_DIALOG_MAX_WIDTH}
                            fullWidth
                            fullScreen={isMobile}
                            PaperProps={EVENT_DIALOG_PAPER_SX}
                        >
                            <DetailScreenHeader
                                title={selectedReservation ? `Buchung #${selectedReservation.id}` : 'Neue Buchung erstellen'}
                                onClose={handleClose}
                            />
                            <DialogContent sx={{ p: 0, backgroundColor: 'var(--pvta-bg)' }}>
                                <ReservationEdit onClose={handleClose} initialReservation={selectedReservation} showHeading={false} />
                            </DialogContent>
                        </Dialog>
                        <AuditInfoDialog
                            open={infoCandidate !== null}
                            onClose={() => setInfoCandidate(null)}
                            title="Buchungsinformationen"
                            createdBy={infoCandidate?.created_by}
                            createdAt={infoCandidate?.created_at}
                            updatedBy={infoCandidate?.updated_by}
                            updatedAt={infoCandidate?.updated_at}
                            deletedBy={infoCandidate?.deleted_by}
                            deletedAt={infoCandidate?.deleted_at}
                        />
                    </>
                )}
            </Container>
        </Box>
    );
};

export default ReservationOverview;
